import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

// Attach token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ Location Search (Google Places via Frontend) ============

// Google Places service instance (initialized lazily)
let googlePlacesService = null;
let googleGeocoderService = null;
let googleAvailable = null; // null = unknown, true/false after first check

function getPlacesService() {
  if (googleAvailable === false) return null;
  try {
    if (googlePlacesService && window.google?.maps?.places?.AutocompleteService) return googlePlacesService;
    if (window.google?.maps?.places?.AutocompleteService) {
      googlePlacesService = new window.google.maps.places.AutocompleteService();
      return googlePlacesService;
    }
  } catch { /* ignore */ }
  return null;
}

function getGeocoderService() {
  if (googleGeocoderService && window.google?.maps) return googleGeocoderService;
  if (window.google?.maps) {
    googleGeocoderService = new window.google.maps.Geocoder();
    return googleGeocoderService;
  }
  return null;
}

function getPlacesDetails(placeId) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.places) { reject('No Google Maps'); return; }
    const div = document.createElement('div');
    const svc = new window.google.maps.places.PlacesService(div);
    svc.getDetails({ placeId, fields: ['geometry', 'formatted_address', 'name'] }, (result, status) => {
      if (status === 'OK' && result) resolve(result);
      else reject(status);
    });
  });
}

export async function searchPlaces(query) {
  if (!query || query.length < 2) return [];

  // Check if input is coordinates
  const coordMatch = query.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (coordMatch) {
    const lat = parseFloat(coordMatch[1]);
    const lng = parseFloat(coordMatch[2]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      const data = await reverseGeocode(lat, lng);
      return [{
        place_id: `coord_${lat}_${lng}`,
        description: data.address,
        name: data.address.split(',')[0],
        secondary: data.address.split(',').slice(1, 3).join(',').trim(),
        lat, lng, type: 'coordinates',
      }];
    }
  }

  // Try Google Places Autocomplete (frontend)
  const svc = getPlacesService();
  if (svc) {
    try {
      const predictions = await Promise.race([
        new Promise((resolve, reject) => {
          svc.getPlacePredictions({ input: query, language: 'de' }, (preds, status) => {
            if (status === 'OK' && preds?.length > 0) resolve(preds);
            else reject(status);
          });
        }),
        new Promise((_, reject) => setTimeout(() => reject('timeout'), 2000)),
      ]);
      const results = [];
      for (const p of predictions.slice(0, 6)) {
        try {
          const detail = await getPlacesDetails(p.place_id);
          if (detail.geometry?.location) {
            results.push({
              place_id: p.place_id,
              description: p.description,
              name: p.structured_formatting?.main_text || p.description.split(',')[0],
              secondary: p.structured_formatting?.secondary_text || '',
              lat: detail.geometry.location.lat(),
              lng: detail.geometry.location.lng(),
              type: 'google',
            });
          }
        } catch { /* skip individual detail */ }
      }
      if (results.length > 0) return results;
    } catch (err) {
      console.log('Google Places unavailable, using Nominatim:', err);
      googleAvailable = false;
    }
  }

  // Fallback to Nominatim
  try {
    const res = await axios.get('https://nominatim.openstreetmap.org/search', {
      params: { q: query, format: 'json', addressdetails: 1, limit: 8, 'accept-language': 'de,en' },
      headers: { 'User-Agent': '366Transfer/1.0' },
    });
    return res.data.map((item) => ({
      place_id: item.place_id.toString(),
      description: item.display_name,
      name: item.name || item.display_name.split(',')[0],
      secondary: item.display_name.split(',').slice(1, 3).join(',').trim(),
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      type: item.type,
    }));
  } catch {
    return [];
  }
}

// Reverse geocode
export async function reverseGeocode(lat, lng) {
  const geocoder = getGeocoderService();
  if (geocoder) {
    try {
      const result = await Promise.race([
        new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) resolve(results[0]);
            else reject(status);
          });
        }),
        new Promise((_, reject) => setTimeout(() => reject('timeout'), 2000)),
      ]);
      return { address: result.formatted_address, lat, lng, place_id: result.place_id };
    } catch { /* fall through */ }
  }
  // Fallback to Nominatim
  try {
    const res = await axios.get('https://nominatim.openstreetmap.org/reverse', {
      params: { lat, lon: lng, format: 'json', 'accept-language': 'de,en' },
      headers: { 'User-Agent': '366Transfer/1.0' },
    });
    return { address: res.data?.display_name || `${lat}, ${lng}`, lat, lng };
  } catch {
    return { address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng };
  }
}

// ============ Distance Calculation ============
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.asin(Math.sqrt(a));
  const distance = Math.round(R * c * 10) / 10;
  const duration = Math.round((distance / 60) * 60);
  const durationText =
    duration >= 60
      ? `${Math.floor(duration / 60)} Std. ${duration % 60} Min.`
      : `${duration} Min.`;
  return { distance, duration, distanceText: `${distance} km`, durationText };
}
